import React, { useMemo, useRef, useState } from 'react';
import { Select } from 'chakra-react-select';
import { useQuery } from '@tanstack/react-query';
import { ICompany, useCompanies } from '../../../../hooks/useCompanies';
import { IData } from '../../../../types/shared';

// Empresa vinculada ao estatuto (T0-D #502) — para o estatuto aparecer na Área VIP do
// cliente. Molde do CartorioSelect (react-query + debounce + chakra-react-select), porém
// só de SELEÇÃO (empresa não se cadastra por aqui).

export interface IEmpresaLite {
  id: number;
  razao_social: string;
  cnpj?: string;
}

// Formata um CNPJ (só dígitos) na máscara 00.000.000/0000-00; devolve o original se
// não tiver 14 dígitos (mantém robusto p/ dados incompletos).
const formatCnpj = (cnpj?: string): string => {
  if (!cnpj) return '';
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

interface EmpresaSelectProps {
  value: IEmpresaLite | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (empresa: IEmpresaLite | null) => void;
}

interface EmpresaOption {
  value: number;
  label: string;
  empresa: IEmpresaLite;
}

export const EmpresaSelect: React.FC<EmpresaSelectProps> = ({ value, onChange }) => {
  const { getCompanies } = useCompanies();

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const onInputChange = (val: string) => {
    setSearch(val);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(val), 400);
  };

  const { data, isFetching } = useQuery<IData<ICompany[]>>({
    // `search` = busca flexível no backend (nome + razão social + CNPJ, ignorando a
    // formatação do CNPJ) — permite pesquisar tanto por nome quanto por CNPJ.
    queryKey: ['empresas-select', { search: debounced || undefined, page: 1, size: 20 }],
    queryFn: getCompanies,
    enabled: menuOpen,
  });

  const options: EmpresaOption[] = useMemo(
    () =>
      (data?.data?.items ?? []).map(c => {
        const cnpjFmt = formatCnpj(c.cnpj);
        return {
          value: c.id,
          // Exibe razão social + CNPJ no dropdown p/ o operador conferir a empresa.
          label: cnpjFmt ? `${c.razao_social} — ${cnpjFmt}` : c.razao_social,
          empresa: { id: c.id, razao_social: c.razao_social, cnpj: c.cnpj },
        };
      }),
    [data],
  );

  return (
    <Select<EmpresaOption>
      placeholder="Buscar empresa por razão social ou CNPJ…"
      options={options}
      value={
        value
          ? {
              value: value.id,
              label: value.cnpj
                ? `${value.razao_social} — ${formatCnpj(value.cnpj)}`
                : value.razao_social,
              empresa: value,
            }
          : null
      }
      inputValue={search}
      onInputChange={onInputChange}
      onMenuOpen={() => setMenuOpen(true)}
      onMenuClose={() => setMenuOpen(false)}
      onChange={opt => onChange(opt ? opt.empresa : null)}
      isLoading={isFetching}
      isClearable
      filterOption={null}
      noOptionsMessage={() => (debounced ? 'Nenhuma empresa encontrada' : 'Digite para buscar')}
      loadingMessage={() => 'Buscando…'}
      size="sm"
    />
  );
};
